import { useId } from 'react';
import { type Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Alert from '@/components/atoms/Alert';
import FormField from '@/components/atoms/FormField';
import SecretInput from '@/components/atoms/SecretInput';
import type { ProviderGroup, ProviderSchema } from '@/domain/providerConfig';
import { errorKeyOf, fieldLabelSuffix } from '@/routes/shared/ProviderConfig/ProviderSchemaField';
import { secretFieldsOf } from '@/routes/shared/ProviderConfig/schemaFields';

import type { ProviderFormValues } from '../form/formModel';

import styles from './sections.module.scss';

export interface CredentialsSectionProps {
    schema: ProviderSchema;
    group: ProviderGroup | null;
    control: Control<ProviderFormValues>;
}

/** The site's secrets: what is typed here goes to every pen of the provider. */
function CredentialsSection({ schema, group, control }: CredentialsSectionProps) {
    const { t } = useTranslation();
    // React's ids hold colons, which break the `#id` selectors jsdom and some libraries build.
    const id = useId().replaceAll(':', '');
    const secrets = secretFieldsOf(schema);
    if (secrets.length === 0) return null;

    const headingId = `${id}-heading`;
    const varies = secrets.some(({ name }) => group?.credentials[name]?.state === 'varies');

    return (
        <section className={styles.section} aria-labelledby={headingId}>
            <h3 id={headingId} className={styles.heading}>
                {t('providerConfig.sections.credentials')}
            </h3>
            <p className={styles.note}>{t('providerConfig.credentials.forAllPens')}</p>
            {varies && (
                // What is stored, not news: nothing to announce.
                <Alert tone="warning" size="small" live="off">
                    {t('providerConfig.credentials.varies')}
                </Alert>
            )}
            <div className={styles.fields}>
                {secrets.map(({ name, labelKey, required }) => {
                    const status = group?.credentials[name];
                    return (
                        <Controller
                            key={name}
                            control={control}
                            name={`secrets.${name}`}
                            render={({ field, fieldState }) => (
                                <FormField
                                    label={t(labelKey)}
                                    // Kept free of "password", which password managers fill.
                                    htmlFor={`${id}-site-${name}`}
                                    labelSuffix={fieldLabelSuffix({ optional: !required }, t)}
                                    error={
                                        fieldState.error
                                            ? t(errorKeyOf(fieldState.error.message))
                                            : null
                                    }>
                                    <SecretInput
                                        required={required}
                                        status={status?.state ?? 'notSet'}
                                        changedAt={status?.changedAt ?? null}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                </FormField>
                            )}
                        />
                    );
                })}
            </div>
        </section>
    );
}

export default CredentialsSection;
export { CredentialsSection };
